/**
 * CÓPIA GÊMEA — este arquivo existe idêntico em dois repositórios:
 *   whiz-server: src/modules/rodizio/cobertura/rodizio-logic.ts
 *   whiz-front:  src/utils/rodizio/rodizio-logic.ts
 * O servidor usa para decidir quem está "em turno" no sorteio e para gravar o
 * retrato de cada execução; o front usa para desenhar mapa, simulador, pontos
 * de atenção e escala efetiva. Se as duas cópias divergirem, a tela passa a
 * mentir sobre o que o rodízio faz. Altere as duas no mesmo par de PRs.
 *
 * Origem: handoff de design do rodízio (rodizio-logic.ts), portado sem mudança
 * de comportamento. Único acréscimo: `Atendente.semJornada` + motivo
 * `SEM_JORNADA`, porque no Whiz "não tem jornada gravada" é um estado próprio
 * (o atendente nunca configurou), diferente de "folga neste dia".
 */
/**
 * Rodízio coverage logic — pure, framework-agnostic, no I/O.
 *
 * Everything here works in "inbox-local minutes": minutes since 00:00 of a weekday,
 * in the inbox's timezone (0..1440). Convert real timestamps with `nowInTimezone`.
 *
 * Rules (as specified by product):
 *  - Canal rodízio DESATIVADO   → nobody is auto-assigned; atendimentos stay in the queue.
 *  - Canal rodízio NORMAL       → rotates between ALL atendentes of the canal, any time,
 *                                  ignoring inbox hours and atendente schedules.
 *  - Canal rodízio POR_HORARIO  → rotates between atendentes of the canal who are "em turno":
 *        em turno = atendente day active AND inbox day active
 *                   AND minute within [max(atStart, inboxStart), min(atEnd, inboxEnd))
 *                   AND minute NOT within the atendente's intervalo.
 *     If nobody is em turno → FALLBACK to NORMAL rodízio between ALL atendentes of the canal.
 *  - Only the inbox's start/end and active days limit atendentes. The inbox's own intervalo,
 *    special dates and atendente online status are NOT considered (product decision).
 *  - Ranges are half-open: [start, end).
 */

export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6; // 0 = Domingo … 6 = Sábado
export type Minute = number; // 0..1440, inbox-local
export type Range = [Minute, Minute];

export type RodizioMode = 'DESATIVADO' | 'NORMAL' | 'POR_HORARIO';

export interface DaySchedule {
  ativo: boolean;
  inicio: string; // "HH:MM"
  fim: string; // "HH:MM"
  intervaloInicio?: string | null;
  intervaloFim?: string | null;
}

export interface Inbox {
  id: string;
  nome: string;
  timezone: string; // IANA, e.g. "America/Sao_Paulo"
  horario: DaySchedule[]; // length 7, index = Weekday
}

export interface Atendente {
  id: string;
  nome: string;
  horario: DaySchedule[]; // length 7, index = Weekday
  /** Whiz: nenhuma linha de jornada gravada neste inbox. `horario` vem todo inativo. */
  semJornada?: boolean;
}

export interface Canal {
  id: string;
  nome: string;
  rodizio: RodizioMode;
  atendenteIds: string[]; // order = rotation order
}

export const SLOT_MINUTES = 15;
export const DAY_MINUTES = 1440;

/* ------------------------------------------------------------------ */
/* time helpers                                                        */
/* ------------------------------------------------------------------ */

export function toMinutes(hhmm: string | null | undefined): Minute | null {
  if (!hhmm) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const v = Number(m[1]) * 60 + Number(m[2]);
  return v >= 0 && v <= DAY_MINUTES ? v : null;
}

export function formatMinutes(v: Minute): string {
  const r = Math.round(v);
  return `${String(Math.floor(r / 60)).padStart(2, '0')}:${String(r % 60).padStart(2, '0')}`;
}

/** "1h30", "45min", "2h" */
export function formatDuration(v: Minute): string {
  const r = Math.round(v), h = Math.floor(r / 60), m = r % 60;
  if (h && m) return `${h}h${String(m).padStart(2, '0')}`;
  return h ? `${h}h` : `${m}min`;
}

export const inRange = (t: Minute, r: Range | null | undefined): boolean => !!r && t >= r[0] && t < r[1];

/** Current weekday + minute in the inbox's timezone. */
export function nowInTimezone(timezone: string, date: Date = new Date()): { day: Weekday; minute: Minute } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone, weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(date);
  const get = (k: string) => parts.find(p => p.type === k)!.value;
  const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday')) as Weekday;
  return { day, minute: (Number(get('hour')) % 24) * 60 + Number(get('minute')) };
}

/* ------------------------------------------------------------------ */
/* inbox + atendente windows                                           */
/* ------------------------------------------------------------------ */

/** Inbox open window for a weekday, or null if closed/invalid. */
export function inboxWindow(inbox: Inbox, day: Weekday): Range | null {
  const d = inbox.horario[day];
  if (!d || !d.ativo) return null;
  const s = toMinutes(d.inicio), e = toMinutes(d.fim);
  return s != null && e != null && e > s ? [s, e] : null;
}

export const isInboxOpen = (inbox: Inbox, day: Weekday, t: Minute) => inRange(t, inboxWindow(inbox, day));

export interface EffectiveDay {
  /** atendente's own configured window (null if day off / invalid) */
  configurado: Range | null;
  /** windows in which the atendente is "em turno" for POR_HORARIO (clipped by inbox, minus intervalo) */
  efetivo: Range[];
  /** parts of `configurado` outside the inbox limits (whole window when the inbox day is closed) */
  cortado: Range[];
  /** intervalo, clipped to the effective window (null if none applies) */
  intervalo: Range | null;
  /** atendente day active but inbox day closed */
  diaBloqueado: boolean;
  /** day active but inicio/fim invalid (fim <= inicio, unparsable) */
  escalaInvalida: boolean;
}

export function effectiveDay(inbox: Inbox, atendente: Atendente, day: Weekday): EffectiveDay {
  const ad = atendente.horario[day];
  const r: EffectiveDay = { configurado: null, efetivo: [], cortado: [], intervalo: null, diaBloqueado: false, escalaInvalida: false };
  if (!ad || !ad.ativo) return r;
  const s = toMinutes(ad.inicio), e = toMinutes(ad.fim);
  if (s == null || e == null || e <= s) { r.escalaInvalida = true; return r; }
  r.configurado = [s, e];

  const iw = inboxWindow(inbox, day);
  if (!iw) { r.diaBloqueado = true; r.cortado = [[s, e]]; return r; }

  if (s < Math.min(e, iw[0])) r.cortado.push([s, Math.min(e, iw[0])]);
  if (e > Math.max(s, iw[1])) r.cortado.push([Math.max(s, iw[1]), e]);

  const ws = Math.max(s, iw[0]), we = Math.min(e, iw[1]);
  if (we <= ws) return r;

  let segs: Range[] = [[ws, we]];
  const bs = toMinutes(ad.intervaloInicio), be = toMinutes(ad.intervaloFim);
  if (bs != null && be != null && be > bs) {
    const cs = Math.max(bs, ws), ce = Math.min(be, we);
    if (ce > cs) {
      r.intervalo = [cs, ce];
      segs = [];
      if (cs > ws) segs.push([ws, cs]);
      if (we > ce) segs.push([ce, we]);
    }
  }
  r.efetivo = segs;
  return r;
}

/* ------------------------------------------------------------------ */
/* who receives                                                        */
/* ------------------------------------------------------------------ */

export type PoolKind = 'SEM_RODIZIO' | 'NORMAL' | 'POR_HORARIO' | 'FALLBACK';

export type MotivoAusencia =
  | 'FOLGA'              // atendente day inactive
  | 'INBOX_FECHADO'      // atendente day active, inbox day closed
  | 'FORA_LIMITE_INBOX'  // minute inside atendente schedule but outside inbox limits
  | 'INTERVALO'
  | 'ANTES_DO_TURNO'
  | 'APOS_TURNO'
  | 'ESCALA_INVALIDA'
  | 'SEM_JORNADA';       // Whiz: atendente sem jornada gravada no inbox

export interface Ausente {
  atendente: Atendente;
  motivo: MotivoAusencia;
  /** minute the condition ends today, when known (e.g. intervalo end, turno start) */
  ate: Minute | null;
}

export interface Recebedor {
  atendente: Atendente;
  /** for POR_HORARIO: end of the current effective window; otherwise null */
  emTurnoAte: Minute | null;
}

export interface Pool {
  kind: PoolKind;
  inboxAberto: boolean;
  /** who a new atendimento can be assigned to at this minute (empty for SEM_RODIZIO) */
  recebedores: Recebedor[];
  /** POR_HORARIO / FALLBACK only: canal members not em turno, with why */
  ausentes: Ausente[];
  membros: Atendente[];
}

export function membersOf(canal: Canal, atendentes: Atendente[]): Atendente[] {
  const byId = new Map(atendentes.map(a => [a.id, a]));
  return canal.atendenteIds.map(id => byId.get(id)).filter((a): a is Atendente => !!a);
}

export function motivoAusencia(inbox: Inbox, a: Atendente, day: Weekday, t: Minute): { motivo: MotivoAusencia; ate: Minute | null } {
  const ad = a.horario[day];
  const e = effectiveDay(inbox, a, day);
  if (a.semJornada) return { motivo: 'SEM_JORNADA', ate: null };
  if (!ad || !ad.ativo) return { motivo: 'FOLGA', ate: null };
  if (e.escalaInvalida) return { motivo: 'ESCALA_INVALIDA', ate: null };
  if (e.diaBloqueado) return { motivo: 'INBOX_FECHADO', ate: null };
  const c = e.cortado.find(r => inRange(t, r));
  if (c) return { motivo: 'FORA_LIMITE_INBOX', ate: e.efetivo.find(s => s[0] >= c[1])?.[0] ?? null };
  if (inRange(t, e.intervalo)) return { motivo: 'INTERVALO', ate: e.intervalo![1] };
  const next = e.efetivo.find(s => s[0] > t);
  if (next) return { motivo: 'ANTES_DO_TURNO', ate: next[0] };
  return { motivo: 'APOS_TURNO', ate: null };
}

export function poolAt(inbox: Inbox, canal: Canal, atendentes: Atendente[], day: Weekday, t: Minute): Pool {
  const membros = membersOf(canal, atendentes);
  const inboxAberto = isInboxOpen(inbox, day, t);
  if (canal.rodizio === 'DESATIVADO') return { kind: 'SEM_RODIZIO', inboxAberto, recebedores: [], ausentes: [], membros };
  if (canal.rodizio === 'NORMAL')
    return { kind: 'NORMAL', inboxAberto, recebedores: membros.map(a => ({ atendente: a, emTurnoAte: null })), ausentes: [], membros };

  const emTurno: Recebedor[] = [];
  const ausentes: Ausente[] = [];
  for (const a of membros) {
    const seg = effectiveDay(inbox, a, day).efetivo.find(s => inRange(t, s));
    if (seg) emTurno.push({ atendente: a, emTurnoAte: seg[1] });
    else ausentes.push({ atendente: a, ...motivoAusencia(inbox, a, day, t) });
  }
  if (emTurno.length) return { kind: 'POR_HORARIO', inboxAberto, recebedores: emTurno, ausentes, membros };
  return { kind: 'FALLBACK', inboxAberto, recebedores: membros.map(a => ({ atendente: a, emTurnoAte: null })), ausentes, membros };
}

/* ------------------------------------------------------------------ */
/* spans + heatmap                                                     */
/* ------------------------------------------------------------------ */

export interface Span {
  inicio: Minute;
  fim: Minute;
  kind: PoolKind;
  inboxAberto: boolean;
  recebedorIds: string[];
}

/** Exact, merged spans over a day where the assignment pool is constant. */
export function channelSpans(inbox: Inbox, canal: Canal, atendentes: Atendente[], day: Weekday): Span[] {
  const b = new Set<number>([0, DAY_MINUTES]);
  const iw = inboxWindow(inbox, day);
  if (iw) { b.add(iw[0]); b.add(iw[1]); }
  if (canal.rodizio === 'POR_HORARIO')
    for (const a of membersOf(canal, atendentes))
      for (const [s, e] of effectiveDay(inbox, a, day).efetivo) { b.add(s); b.add(e); }
  const pts = [...b].sort((x, y) => x - y);
  const out: Span[] = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const s = pts[i], e = pts[i + 1];
    if (e <= s) continue;
    const p = poolAt(inbox, canal, atendentes, day, (s + e) / 2);
    const ids = p.recebedores.map(r => r.atendente.id);
    const last = out[out.length - 1];
    if (last && last.fim === s && last.kind === p.kind && last.inboxAberto === p.inboxAberto && last.recebedorIds.join() === ids.join()) last.fim = e;
    else out.push({ inicio: s, fim: e, kind: p.kind, inboxAberto: p.inboxAberto, recebedorIds: ids });
  }
  return out;
}

export type HeatCellState = 'SEM_RODIZIO' | 'NORMAL' | 'POR_HORARIO' | 'FALLBACK_EXPEDIENTE' | 'FALLBACK_FORA';

export interface HeatCell {
  day: Weekday;
  inicio: Minute; // slot start
  state: HeatCellState;
  /** number of recebedores (for POR_HORARIO: em turno) */
  count: number;
  /** canal member count, for intensity = count / total */
  total: number;
  recebedorIds: string[];
}

/**
 * 7 × (1440 / slotMinutes) grid. A slot takes the state at its midpoint.
 * NOTE: with 15-min slots, schedules on non-quarter minutes are approximated; spans are exact.
 */
export function heatmap(inbox: Inbox, canal: Canal, atendentes: Atendente[], slotMinutes = SLOT_MINUTES): HeatCell[][] {
  const total = membersOf(canal, atendentes).length;
  const grid: HeatCell[][] = [];
  for (let d = 0 as Weekday; d < 7; d = (d + 1) as Weekday) {
    const row: HeatCell[] = [];
    for (let s = 0; s < DAY_MINUTES; s += slotMinutes) {
      const p = poolAt(inbox, canal, atendentes, d, s + slotMinutes / 2);
      const state: HeatCellState = p.kind === 'FALLBACK' ? (p.inboxAberto ? 'FALLBACK_EXPEDIENTE' : 'FALLBACK_FORA') : p.kind;
      row.push({ day: d, inicio: s, state, count: p.recebedores.length, total, recebedorIds: p.recebedores.map(r => r.atendente.id) });
    }
    grid.push(row);
  }
  return grid;
}

export interface CoverageStats {
  expedienteMin: Minute;   // total inbox-open minutes in the week
  cobertoMin: Minute;      // inbox-open minutes with someone em turno (or NORMAL)
  fallbackMin: Minute;     // inbox-open minutes in FALLBACK
  umaPessoaMin: Minute;    // inbox-open minutes POR_HORARIO with exactly one em turno
  coberturaPct: number;    // 0..100, rounded
}

export function coverageStats(inbox: Inbox, canal: Canal, atendentes: Atendente[]): CoverageStats {
  let exp = 0, cob = 0, fb = 0, one = 0;
  for (let d = 0 as Weekday; d < 7; d = (d + 1) as Weekday)
    for (const sp of channelSpans(inbox, canal, atendentes, d)) {
      if (!sp.inboxAberto) continue;
      const w = sp.fim - sp.inicio;
      exp += w;
      if (sp.kind === 'POR_HORARIO' || sp.kind === 'NORMAL') cob += w;
      if (sp.kind === 'FALLBACK') fb += w;
      if (sp.kind === 'POR_HORARIO' && sp.recebedorIds.length === 1) one += w;
    }
  return { expedienteMin: exp, cobertoMin: cob, fallbackMin: fb, umaPessoaMin: one, coberturaPct: exp ? Math.round((cob / exp) * 100) : 0 };
}

/* ------------------------------------------------------------------ */
/* pontos de atenção                                                   */
/* ------------------------------------------------------------------ */

export type PontoTipo =
  | 'LACUNA_EXPEDIENTE'    // POR_HORARIO canal falls back during inbox hours
  | 'ESCALA_CORTADA'       // part of an atendente's schedule is outside inbox limits
  | 'DIA_BLOQUEADO'        // atendente day active, inbox closed
  | 'SEM_HORARIO_EFETIVO'; // atendente in a POR_HORARIO canal with zero effective minutes all week

export interface PontoAtencao {
  id: string;
  tipo: PontoTipo;
  severidade: 'aviso' | 'info';
  /** canais affected: the canal for LACUNA; every POR_HORARIO canal the atendente belongs to otherwise */
  canalIds: string[];
  atendenteId?: string;
  dias: Weekday[];
  ranges: Range[]; // same ranges on every listed day
}

/**
 * Grouped by identical ranges across days, so "Seg–Qui 12:00–12:30" is one item.
 * Only atendentes in at least one POR_HORARIO canal produce schedule items
 * (their schedule has no effect anywhere else).
 */
export function pontosDeAtencao(inbox: Inbox, canais: Canal[], atendentes: Atendente[]): PontoAtencao[] {
  const out: PontoAtencao[] = [];
  const porHorario = canais.filter(c => c.rodizio === 'POR_HORARIO');

  for (const c of porHorario) {
    const groups = new Map<string, PontoAtencao>();
    for (let d = 0 as Weekday; d < 7; d = (d + 1) as Weekday)
      for (const sp of channelSpans(inbox, c, atendentes, d)) {
        if (sp.kind !== 'FALLBACK' || !sp.inboxAberto) continue;
        const k = `${sp.inicio}-${sp.fim}`;
        const g = groups.get(k) ?? { id: `lacuna:${c.id}:${k}`, tipo: 'LACUNA_EXPEDIENTE', severidade: 'aviso', canalIds: [c.id], dias: [], ranges: [[sp.inicio, sp.fim]] } as PontoAtencao;
        g.dias.push(d);
        groups.set(k, g);
      }
    out.push(...groups.values());
  }

  for (const a of atendentes) {
    const canalIds = porHorario.filter(c => c.atendenteIds.includes(a.id)).map(c => c.id);
    if (!canalIds.length) continue;
    const cuts = new Map<string, PontoAtencao>();
    const blocked: Weekday[] = [];
    let effective = 0;
    for (let d = 0 as Weekday; d < 7; d = (d + 1) as Weekday) {
      const e = effectiveDay(inbox, a, d);
      effective += e.efetivo.reduce((s, r) => s + r[1] - r[0], 0);
      if (e.diaBloqueado) { blocked.push(d); continue; }
      if (!e.cortado.length) continue;
      const k = e.cortado.map(r => r.join('-')).join(',');
      const g = cuts.get(k) ?? { id: `cortada:${a.id}:${k}`, tipo: 'ESCALA_CORTADA', severidade: 'info', canalIds, atendenteId: a.id, dias: [], ranges: e.cortado } as PontoAtencao;
      g.dias.push(d);
      cuts.set(k, g);
    }
    out.push(...cuts.values());
    if (blocked.length) out.push({ id: `bloqueado:${a.id}`, tipo: 'DIA_BLOQUEADO', severidade: 'info', canalIds, atendenteId: a.id, dias: blocked, ranges: [] });
    if (!effective) out.push({ id: `sem-efetivo:${a.id}`, tipo: 'SEM_HORARIO_EFETIVO', severidade: 'aviso', canalIds, atendenteId: a.id, dias: [], ranges: [] });
  }
  return out;
}

/** Highest severity for a canal's warning icon on the canais list (null = no icon). */
export function canalSeveridade(pontos: PontoAtencao[], canalId: string): 'aviso' | 'info' | null {
  const mine = pontos.filter(p => p.canalIds.includes(canalId));
  if (mine.some(p => p.severidade === 'aviso')) return 'aviso';
  return mine.length ? 'info' : null;
}

/* ------------------------------------------------------------------ */
/* atendente week summary (Escala efetiva component)                  */
/* ------------------------------------------------------------------ */

export interface AtendenteResumo {
  efetivoMin: Minute;     // minutes em turno per week (POR_HORARIO meaning)
  configuradoMin: Minute; // configured minutes minus intervalo
  cortadoMin: Minute;     // configured minutes outside inbox limits
  dias: EffectiveDay[];   // index = Weekday
  canaisPorHorario: string[];
  canaisNormal: string[];
  canaisDesativado: string[];
  /** inbox-hour fallback minutes per POR_HORARIO canal this atendente belongs to */
  fallbackPorCanal: { canalId: string; minutos: Minute }[];
}

export function atendenteResumo(inbox: Inbox, atendente: Atendente, canais: Canal[], atendentes: Atendente[]): AtendenteResumo {
  const dias = ([0, 1, 2, 3, 4, 5, 6] as Weekday[]).map(d => effectiveDay(inbox, atendente, d));
  let ef = 0, cf = 0, ct = 0;
  dias.forEach((e, d) => {
    e.efetivo.forEach(r => (ef += r[1] - r[0]));
    e.cortado.forEach(r => (ct += r[1] - r[0]));
    if (e.configurado) {
      const ad = atendente.horario[d];
      const bs = toMinutes(ad.intervaloInicio), be = toMinutes(ad.intervaloFim);
      const brk = bs != null && be != null && be > bs ? Math.max(0, Math.min(be, e.configurado[1]) - Math.max(bs, e.configurado[0])) : 0;
      cf += e.configurado[1] - e.configurado[0] - brk;
    }
  });
  const mine = canais.filter(c => c.atendenteIds.includes(atendente.id));
  // `atendente` may be an unsaved draft: substitute it so spans reflect the form state
  const pool = atendentes.map(a => (a.id === atendente.id ? atendente : a));
  const fallbackPorCanal = mine.filter(c => c.rodizio === 'POR_HORARIO').map(c => {
    let m = 0;
    for (let d = 0 as Weekday; d < 7; d = (d + 1) as Weekday)
      for (const sp of channelSpans(inbox, c, pool, d)) if (sp.kind === 'FALLBACK' && sp.inboxAberto) m += sp.fim - sp.inicio;
    return { canalId: c.id, minutos: m };
  }).filter(x => x.minutos > 0);
  return {
    efetivoMin: ef, configuradoMin: cf, cortadoMin: ct, dias,
    canaisPorHorario: mine.filter(c => c.rodizio === 'POR_HORARIO').map(c => c.id),
    canaisNormal: mine.filter(c => c.rodizio === 'NORMAL').map(c => c.id),
    canaisDesativado: mine.filter(c => c.rodizio === 'DESATIVADO').map(c => c.id),
    fallbackPorCanal,
  };
}

/* ------------------------------------------------------------------ */
/* copy (pt-BR)                                                        */
/* ------------------------------------------------------------------ */

export const WEEKDAY_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'] as const;
export const WEEKDAY_LONG = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'] as const;

/** [1,2,3,4] → "Seg–Qui"; [1,3] → "Seg, Qua"; [6,0] → "Dom, Sáb" */
export function formatDias(dias: Weekday[]): string {
  const ds = [...new Set(dias)].sort((a, b) => a - b);
  const runs: [number, number][] = [];
  ds.forEach(d => { const r = runs[runs.length - 1]; if (r && r[1] === d - 1) r[1] = d; else runs.push([d, d]); });
  return runs.map(([a, b]) => a === b ? WEEKDAY_SHORT[a] : b === a + 1 ? `${WEEKDAY_SHORT[a]}, ${WEEKDAY_SHORT[b]}` : `${WEEKDAY_SHORT[a]}–${WEEKDAY_SHORT[b]}`).join(', ');
}

export const MODE_LABEL: Record<RodizioMode, string> = {
  DESATIVADO: 'Sem rodízio',
  NORMAL: 'Rodízio normal',
  POR_HORARIO: 'Por horário',
};

export function motivoLabel(m: MotivoAusencia, ate: Minute | null): string {
  switch (m) {
    case 'FOLGA': return 'Folga neste dia';
    case 'INBOX_FECHADO': return 'Inbox fechado neste dia';
    case 'FORA_LIMITE_INBOX': return 'Fora do limite do inbox';
    case 'INTERVALO': return ate != null ? `Em intervalo até ${formatMinutes(ate)}` : 'Em intervalo';
    case 'ANTES_DO_TURNO': return ate != null ? `Turno começa ${formatMinutes(ate)}` : 'Antes do turno';
    case 'APOS_TURNO': return 'Turno encerrado';
    case 'ESCALA_INVALIDA': return 'Escala inválida';
    case 'SEM_JORNADA': return 'Sem jornada configurada';
  }
}
